/**
 * RpgIcon — renders a mob / weapon / item / shop icon from its API ``icon_url``.
 *
 * Like product/currency art, the URL comes from object storage; it is drawn into a
 * square box with ``object-fit: contain`` so it is never stretched or cropped. Falls
 * back to a short monospace glyph (first letters of the name) when the URL is empty or
 * the image fails to load.
 */
import { useState } from "react";

import styles from "../rpg.module.css";

export interface RpgIconProps {
  readonly url: string;
  readonly name: string;
  /** Edge size in px. */
  readonly size?: number;
  readonly className?: string;
}

function shortLabel(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return "—";
  }
  return trimmed.slice(0, 3);
}

export function RpgIcon({
  url,
  name,
  size,
  className,
}: RpgIconProps): JSX.Element {
  const [failed, setFailed] = useState(false);
  const showImg = url.trim().length > 0 && !failed;
  const style = size !== undefined ? { width: size, height: size } : undefined;
  return (
    <span
      className={`${styles.iconBox} ${className ?? ""}`}
      style={style}
      aria-hidden="true"
    >
      {showImg ? (
        <img
          src={url}
          alt=""
          className={styles.iconImg}
          draggable={false}
          onError={() => {
            setFailed(true);
          }}
        />
      ) : (
        <span className={styles.iconFallback}>{shortLabel(name)}</span>
      )}
    </span>
  );
}
